import { useMemo } from 'react'
import { filterByExpenseType, filterByPaidBy } from '@/lib/utils'
import {
  aggregateByParentCategory,
  buildCategoryDrilldown,
  classifyCategoriesByExpenseType,
  computeCategoryBaselines,
  computeCategoryDeviations,
  computeDailyPace,
  computeDayOfWeek,
  computeFrequentMerchants,
  computeMonthlyStatistics,
  generateInsights,
} from '@/lib/statistics'
import type {
  CategoryDeviation,
  CategoryDrilldown,
  DailyPacePoint,
  DayOfWeekStat,
  FrequentMerchant,
  Insight,
  MonthlyStatistics,
} from '@/lib/statistics'
import { useTransactions } from './use-transactions'
import { useBudgetStatus } from './use-budgets'
import type { BudgetItem } from './use-budgets'
import { useCategories } from './use-categories'
import { useTrendData } from './use-trend-data'
import type { TrendData } from './use-trend-data'
import { useExpenseType } from '@/lib/expense-type-context'
import { usePaidBy } from '@/lib/paid-by-context'

export type ReportStatistics = {
  // Loading state
  isLoading: boolean

  // Filtered transactions for the current month (scope/expenseType/paidBy applied)
  filteredTx: ReturnType<typeof useTransactions>['data']

  // Headline figures
  stats: MonthlyStatistics
  baselineTotal: number

  // Per-category analysis
  deviations: CategoryDeviation[]
  categoryDrilldown: Map<string, CategoryDrilldown>

  // Behavioral signals
  pace: DailyPacePoint[]
  dayOfWeek: DayOfWeekStat[]
  frequent: FrequentMerchant[]

  // Insights surfaced at the top of the page
  insights: Insight[]

  // Budgets (parent-level) keyed by category name
  budgetMap: Map<string, BudgetItem>
  budgetByCategory: Map<string, number>

  // 6-month trend (passed through from useTrendData for charting)
  trendData: TrendData[]
}

/**
 * Aggregates the transaction / budget / trend data needed by the Report page
 * and runs the statistical helpers in one place. Centralizes the orchestration
 * so the page component only deals with layout and filter state.
 *
 * The current month's transactions are always filtered by the active
 * scope/expenseType/paidBy filters; the past-month classification (used to
 * mark categories as "fixed") deliberately uses the unfiltered past so that
 * 家賃 still reads as "mostly-fixed" even when the user is on the "all" view.
 */
export function useReportStatistics(month: string): ReportStatistics {
  const { expenseType } = useExpenseType()
  const { paidBy } = usePaidBy()

  const { data: transactions = [], isLoading: txLoading } = useTransactions(month)
  const { data: budgetStatus = [], isLoading: budgetLoading } = useBudgetStatus(month, expenseType)
  const { data: categories = [] } = useCategories()
  const {
    trendData,
    monthlyTransactions,
    isLoading: trendLoading,
  } = useTrendData(month, undefined, expenseType, paidBy)

  const filteredTx = useMemo(
    () => filterByPaidBy(filterByExpenseType(transactions, expenseType), paidBy),
    [transactions, expenseType, paidBy],
  )

  const stats = useMemo(() => computeMonthlyStatistics(month, filteredTx), [month, filteredTx])

  const { baselines, baselineTotal, classifications, pastMonthlyTransactions } = useMemo(() => {
    const past = monthlyTransactions.slice(0, Math.max(0, monthlyTransactions.length - 1))
    return {
      baselines: computeCategoryBaselines(past),
      baselineTotal:
        past.length > 0
          ? past.reduce(
              (sum, txs) => sum + txs.reduce((s, t) => s + t.total_amount, 0),
              0,
            ) / past.length
          : 0,
      classifications: classifyCategoriesByExpenseType(past),
      pastMonthlyTransactions: past,
    }
  }, [monthlyTransactions])

  const currentMonthTotalsByCategory = useMemo(
    () => aggregateByParentCategory(filteredTx),
    [filteredTx],
  )

  const deviations = useMemo(
    () => computeCategoryDeviations(currentMonthTotalsByCategory, baselines, classifications),
    [currentMonthTotalsByCategory, baselines, classifications],
  )

  const pace = useMemo(
    () => computeDailyPace(month, filteredTx, pastMonthlyTransactions),
    [month, filteredTx, pastMonthlyTransactions],
  )

  const dayOfWeek = useMemo(() => computeDayOfWeek(month, filteredTx), [month, filteredTx])
  const frequent = useMemo(() => computeFrequentMerchants(filteredTx), [filteredTx])

  const { budgetMap, budgetByCategory, budgetOverages } = useMemo(() => {
    const map = new Map<string, BudgetItem>()
    for (const b of budgetStatus) {
      if (!b.is_subcategory_budget) map.set(b.category_name, b)
    }
    const subByParent = new Map<string, typeof budgetStatus>()
    for (const b of budgetStatus) {
      if (b.is_subcategory_budget && b.parent_category_id) {
        const arr = subByParent.get(b.parent_category_id) ?? []
        arr.push(b)
        subByParent.set(b.parent_category_id, arr)
      }
    }
    for (const [parentId, subs] of subByParent) {
      const parentCat = categories.find((c) => c.id === parentId)
      if (!parentCat) continue
      const budgeted = subs.filter((i) => i.budget_amount > 0)
      const totalBudget = budgeted.reduce((s, i) => s + i.budget_amount, 0)
      const totalSpent = budgeted.reduce((s, i) => s + i.spent_amount, 0)
      map.set(parentCat.name, {
        category_id: parentId,
        category_name: parentCat.name,
        budget_amount: totalBudget,
        spent_amount: totalSpent,
        remaining: totalBudget - totalSpent,
        sort_order: parentCat.sort_order ?? 0,
        color: parentCat.color ?? null,
        parent_category_id: null,
        is_subcategory_budget: false,
      })
    }
    const byCategory = new Map<string, number>()
    const overages: Array<{ category_name: string; over: number }> = []
    for (const [name, item] of map) {
      if (item.budget_amount > 0) {
        byCategory.set(name, item.budget_amount)
        if (item.remaining < 0) {
          overages.push({ category_name: name, over: Math.abs(item.remaining) })
        }
      }
    }
    overages.sort((a, b) => b.over - a.over)
    return { budgetMap: map, budgetByCategory: byCategory, budgetOverages: overages }
  }, [budgetStatus, categories])

  const insights = useMemo(
    () =>
      generateInsights({
        yearMonth: month,
        stats,
        baselineTotal,
        deviations,
        frequent,
        dayOfWeek,
        budgetOverages,
      }),
    [month, stats, baselineTotal, deviations, frequent, dayOfWeek, budgetOverages],
  )

  const categoryDrilldown = useMemo(() => buildCategoryDrilldown(filteredTx), [filteredTx])

  return {
    isLoading: txLoading || budgetLoading || trendLoading,
    filteredTx,
    stats,
    baselineTotal,
    deviations,
    categoryDrilldown,
    pace,
    dayOfWeek,
    frequent,
    insights,
    budgetMap,
    budgetByCategory,
    trendData,
  }
}
